import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRedirectUri, VERSION_CONFIG } from '@/constants/consts';
import NophoneKakaoLogin from '@/containers/auth/NophoneKakaoLogin';
import { useAppDispatch } from '@/redux/store';
import { setUser } from '@/redux/userSlice';
import { cplatApiV2 } from '@/api';
import axios from 'axios';
import { AES } from '@/utils/AES';
import { useLocalStorage } from 'usehooks-ts';
import { ROUTES } from '@/routes';
import { useToast } from '@/hooks/useToast';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';

function KakaoCallback() {
  const [noPhone, setNoPhone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [_, setCplatToken] = useLocalStorage('cplatToken', '');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const code = new URL(window.location.href).searchParams.get('code');
  const grantType = 'authorization_code';
  const CLIENT_ID = import.meta.env.VITE_APP_REST_API_KEY;
  const REDIRECT_URI = getRedirectUri();

  useEffect(() => {
    if (!code) return;

    let access_token = '';
    let kakaoLoginJson = {};
    let kakaoProfileJson = {};

    setIsSubmitting(true);

    // 1. 카카오 토큰 발급
    axios
      .post(
        `https://kauth.kakao.com/oauth/token?grant_type=${grantType}&client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&code=${code}`,
        {},
        {
          headers: {
            'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
          },
        },
      )
      .then((res) => {
        access_token = res.data.access_token;
        kakaoLoginJson = res.data;

        // 2. 유저 정보 조회
        return axios.post(
          `https://kapi.kakao.com/v2/user/me`,
          {},
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
              'Content-type': 'application/x-www-form-urlencoded;charset=utf-8',
            },
          },
        );
      })
      .then((res) => {
        kakaoProfileJson = res.data;
        const kakaoAccount = res.data.kakao_account;
        const phoneNumber = kakaoAccount.phone_number;
        if (!phoneNumber) {
          setNoPhone(true);
          setIsSubmitting(false);
          return;
        }

        // 3. 서비스 약관(마케팅 등) 동의 여부 조회
        axios
          .get(`https://kapi.kakao.com/v2/user/service_terms`, {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          })
          .then((termsRes) => {
            const marketingTerm = termsRes.data.service_terms.find(
              (term) => term.tag === 'marketing_sellerbox',
            );
            const AdAgreeYn = marketingTerm && marketingTerm.agreed ? 'Y' : 'N';

            const convertedKakaoProfile = {
              id: kakaoProfileJson.id,
              phoneNumber: kakaoProfileJson.kakao_account.phone_number || '',
              email: kakaoProfileJson.kakao_account.email || '',
              thumbnailImageUrl: kakaoProfileJson.kakao_account.profile?.thumbnail_image_url || '',
            };

            // 4. payload 생성
            const payload = {
              SnsType: 'kakao',
              Token: '',
              OsType: VERSION_CONFIG.osType,
              CurrentVersion: VERSION_CONFIG.currentVersion,
              kakaoLoginJson: JSON.stringify(kakaoLoginJson),
              kakaoProfileJson: JSON.stringify(convertedKakaoProfile),
              AdAgreeYn,
              SignUpPlatform: 'sellerbox-web',
            };

            // 5. 백엔드 로그인 API 호출 (cplatApiV2 사용)
            cplatApiV2
              .post('/user/sign_in_by_social', payload)
              .then((response) => {
                const result = response.data;
                const { message, data } = result;
                if (
                  message === 'OK-first-access' ||
                  message === 'OK-phone-number-empty' ||
                  message === 'OK'
                ) {
                  // 토큰 암호화 및 저장
                  const encryptedToken = AES.encryptData(data[0].CplatToken);
                  setCplatToken(encryptedToken);
                  // 유저 정보 저장
                  dispatch(setUser(data[0]));
                  // 어드민 분기
                  if (data[0].PhoneNumber === '01093483900') {
                    navigate(ROUTES.admin, { replace: true });
                  } else {
                    navigate(ROUTES.root, { replace: true });
                  }
                  toast({
                    icon: '✅',
                    title: '셀러박스에 로그인 되었어요!',
                    variant: 'dark',
                  });
                } else {
                  // 회원가입 등 분기 필요시 처리
                  toast({
                    icon: '🚨',
                    title: '로그인 처리 중 오류가 발생했습니다. 다시 시도해 주세요.',
                    variant: 'error',
                  });
                  navigate(`${ROUTES.signin}?ctab=init`, { replace: true });
                }
              })
              .catch((error) => {
                toast({
                  icon: '🚨',
                  title: '로그인 처리 중 오류가 발생했습니다. 다시 시도해 주세요.',
                  variant: 'error',
                });
                navigate(`${ROUTES.signin}?ctab=init`, { replace: true });
              })
              .finally(() => {
                setIsSubmitting(false);
              });
          })
          .catch(() => {
            const convertedKakaoProfile = {
              id: kakaoProfileJson.id,
              phoneNumber: kakaoProfileJson.kakao_account.phone_number || '',
              email: kakaoProfileJson.kakao_account.email || '',
              thumbnailImageUrl: kakaoProfileJson.kakao_account.profile?.thumbnail_image_url || '',
            };

            // 약관 동의 조회 실패 시 기본값 'N'
            const payload = {
              SnsType: 'kakao',
              Token: '',
              OsType: VERSION_CONFIG.osType,
              CurrentVersion: VERSION_CONFIG.currentVersion,
              kakaoLoginJson: JSON.stringify(kakaoLoginJson),
              kakaoProfileJson: JSON.stringify(convertedKakaoProfile),
              AdAgreeYn: 'N',
              SignUpPlatform: 'sellerbox-web',
            };
            cplatApiV2
              .post('/user/sign_in_by_social', payload)
              .then((response) => {
                const result = response.data;
                const { message, data } = result;
                const encryptedToken = AES.encryptData(data[0].CplatToken);
                setCplatToken(encryptedToken);
                dispatch(setUser(data[0]));
                navigate(ROUTES.root, { replace: true });
                toast({
                  icon: '✅',
                  title: '셀러박스에 로그인 되었어요!',
                  variant: 'dark',
                });
              })
              .catch((error) => {
                toast({
                  icon: '🚨',
                  title: '로그인 처리 중 오류가 발생했습니다. 다시 시도해 주세요.',
                  variant: 'error',
                });
                navigate(`${ROUTES.signin}?ctab=init`, { replace: true });
              })
              .finally(() => {
                setIsSubmitting(false);
              });
          });
      })
      .catch((err) => {
        toast({
          icon: '🚨',
          title: '카카오 인증 처리 중 오류가 발생했습니다. 다시 시도해 주세요.',
          variant: 'error',
        });
        setIsSubmitting(false);
        navigate(`${ROUTES.signin}?ctab=init`, { replace: true });
      });
  }, [code, CLIENT_ID, REDIRECT_URI, setCplatToken, dispatch, navigate]);

  if (noPhone) {
    return <NophoneKakaoLogin />;
  }

  return <GlobalSpinner />;
}

export default KakaoCallback;
